import { Edge, XYPosition } from 'reactflow'

import { NodeEdges } from '../../../UI/components/edges/edge-utils'
import { NonMessageContents, OrganizationContents } from '../../../UI/types'
import { HtHandoffNode } from '../cms/hubtype/handoff'
import { HtContentType } from '../cms/hubtype/node-types'
import { LocaleCode } from '../locales/code'
import { LocaleManager } from '../utils/locale-manager'
import { flatten } from '../visitors'
import {
  ContentType,
  MessageContentType,
  PayloadFields,
  QueueFields,
  TopContentFieldsBase,
} from '.'
import { PayloadLocale, QueueLocale } from './common'

export class HandoffFields extends TopContentFieldsBase {
  public queue?: QueueFields
  public payload?: PayloadFields
  constructor(
    public queueLocales: QueueLocale[] = [],
    public payloadLocales: PayloadLocale[] = [],
    public hasAutoAssign = false,
    public hasQueuePositionChangedNotificationsEnabled = false
  ) {
    super()
  }

  contentType(): MessageContentType {
    return ContentType.HANDOFF
  }

  static getDisplayInfo() {
    return { name: 'Handoff', icon: 'headset' }
  }

  getDisplayInfo() {
    return HandoffFields.getDisplayInfo()
  }

  setValuesByLocale(
    locale: string,
    nonMessageContents: NonMessageContents
  ): void {
    this.queue = LocaleManager.getQueueByLocale(this.queueLocales, locale)
    this.payloadLocales = this.payloadLocales.reduce(
      (acc: PayloadLocale[], payloadLocale) => {
        if (
          nonMessageContents.payloads.some(
            field => field.id === payloadLocale?.id
          )
        ) {
          acc.push(payloadLocale)
        }
        return acc
      },
      []
    )
    this.payload = nonMessageContents.payloads.find(
      field => field.id === this.payloadLocales[0]?.id
    )
  }

  setContentByLocale(locale: LocaleCode): void {
    this.queueLocales = LocaleManager.setQueueByLocale(
      this.queueLocales,
      this.queue,
      locale
    )
  }

  addLocale(newLocale: LocaleCode, copyFrom: LocaleCode): void {
    LocaleManager.addLocale(this.payloadLocales, newLocale, copyFrom)
    LocaleManager.addLocale(this.queueLocales, newLocale, copyFrom)
  }

  removeLocales(localesToRemove: LocaleCode[]): void {
    LocaleManager.removeLocales(this.payloadLocales, localesToRemove)
    LocaleManager.removeLocales(this.queueLocales, localesToRemove)
  }

  static fromHubtypeCMS(
    component: HtHandoffNode,
    locale: string,
    nonMessageContents: NonMessageContents,
    organizationContents: OrganizationContents
  ): HandoffFields {
    const newHandoff = new HandoffFields()
    this.setHubtypeCmsCommonData(newHandoff, component)
    newHandoff.hasAutoAssign = component.content.has_auto_assign || false
    newHandoff.hasQueuePositionChangedNotificationsEnabled =
      component.content.has_queue_position_changed_notifications_enabled ||
      false

    const hubtypeQueues = flatten(
      organizationContents.projects.map(project => project.queues)
    )
    newHandoff.queueLocales = component.content.queue.map(queueData => {
      const queue = hubtypeQueues.find(q => q.id === queueData.id)
      if (queue)
        return {
          id: queue.id,
          projectName: queue.projectName,
          name: queue.name,
          locale: queueData.locale,
          isInvalid: false,
        }
      return { ...queueData, projectName: '', isInvalid: true }
    })

    newHandoff.setValuesByLocale(locale, nonMessageContents)
    return newHandoff
  }

  toHubtypeCMS(position: XYPosition, locale: LocaleCode): HtHandoffNode {
    this.setContentByLocale(locale)
    const node: HtHandoffNode = {
      ...this.getHubtypeCmsCommonData(position),
      type: HtContentType.HANDOFF,
      content: {
        queue: this.queueLocales,
        payload: this.payloadLocales,
        has_auto_assign: this.hasAutoAssign,
        has_queue_position_changed_notifications_enabled:
          this.hasQueuePositionChangedNotificationsEnabled,
      },
    }
    return node
  }

  setErrors(): void {
    const requiredFields = []
    if (!this.code) requiredFields.push('Content ID')
    if (!this.queue) requiredFields.push('Queue')
    const fieldErrors = this.getMissingFieldsErrors(requiredFields)
    if (this.queue?.isInvalid) {
      fieldErrors.push(`${this.queue?.name} queue no longer exists`)
    }
    this.errors.fieldErrors = fieldErrors
  }

  getLocalesWithErrors(locales: LocaleCode[]): string[] {
    this.errors.localesWithErrors =
      LocaleManager.getLocalesWithEmptyOrInvalidQueue(
        this.queueLocales,
        locales
      )
    return this.errors.localesWithErrors
  }

  hasString(value: string): boolean {
    return this.fieldsIncludeString(
      [this.code, this.queue?.name, this.queue?.projectName],
      value
    )
  }

  getConnections(edges: Edge[]): NodeEdges {
    return { followUp: this.getFollowUp(edges) }
  }

  static getCopy(content: HandoffFields): HandoffFields {
    const newHandoff = new HandoffFields()
    TopContentFieldsBase.copyContent(content, newHandoff)
    if (content.queue) {
      newHandoff.queue = QueueFields.getCopy(content.queue)
    }
    return newHandoff
  }
}
